import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { infoSeqIdCounter } from '../app/middleware';
import { AgentFirstCartEditVia } from '../constants/agentFirstCartEdit';
import { DUMMY_RESTAURANT_CODE, OUT_OF_SESSION } from '../constants';
import {
  AgentTypes,
  CheckResponseTypes,
  CouponResponseTypes,
  EventTypes,
} from '../constants/event';
import { AgentMode } from '../redux/features/agentMode/agentMode.constants';
import { AIStatusColors } from '../redux/features/ai/ai.constants';
import { IAIState, ReusedAnswers } from '../redux/features/ai/ai.props';
import { ITaskRouterSlice } from '../redux/features/taskRouter/taskRouter.props';
import { selectIsWebSocketConnected } from '../selectors/message';
import { readEnvVariable } from '../utils';
import { agent } from '../utils/agent';
import { cyborgAlert } from '../utils/alert';
import { get } from '../utils/api';
import logger from '../utils/logger';
import { ILoyaltyTransmissionMessage } from './cartSlice.props';
import { selectMenuVersion } from './menuSlice';
import { filterBufferedEvents } from './messagingSlice.utils';
import {
  IOrderTransmissionMessage,
  ISendOrderLoyalty,
} from './orderSlice.props';
import { selectStage } from './restaurantSlice';
import { updateNonCriticalCheckScore } from '../redux/features/healthScore/healthScore.slice';
import { NonCriticalChecksNames } from '../redux/features/healthScore/healthScore.constants';
import { IBrandMenuOrderItem, IMUErrorResponse } from '../types/brandMenu';
import { Languages } from '../utils/constants';
import { OrderProcessingMetricData } from './messagingSlice.props';

export enum SessionSource {
  hitl = 'HITL',
  communicator = 'COMMUNICATOR',
}

export enum RestaurantStaffInterventionStatus {
  initial = 'INITIAL',
  open = 'OPEN',
  close = 'CLOSE',
}

export interface TransmissionMessage {
  seq: number;
  id: string;
  event: EventTypes;
  timestamp: string;
  agent_id: string;
  agent_type: AgentTypes;
  session_id: string;
  metadata?: any;
  restaurant_code?: string;
}

export interface IMessageReceived {
  messages: TransmissionMessage[];
  taskRouter?: ITaskRouterSlice;
  isSessionBufferedMessages?: boolean;
  sessionBoundarySessionId?: string;
  ai?: IAIState;
  // HITL-1604: read by the agentMode slice, which resets the mode to Shadow
  // on car_enter only when no order is in flight — an order still being finished
  // must not throw the agent out of Intercept. Passed in rather than selected
  // because reducers cannot reach another slice's state, the same way `ai` and
  // `sessionBoundarySessionId` above are.
  isCartEmpty?: boolean;
  FEATURE_FLAG_FINAL_ORDER_VALIDATION?: boolean;
  FEATURE_FLAG_LOYALTY_SUPPORT?: boolean;
  FEATURE_FLAG_MASK_ORDER_PENDING_ERROR?: boolean;
  // HITL-1497: with secondary ASR on, the committed customer line comes from the
  // `asr_promoted_text` event rather than the `text`/`final` frame, so the
  // in-progress hypothesis must survive the final and be cleared by the
  // promotion instead — otherwise the partial vanishes and nothing replaces it
  // until the promotion lands.
  FEATURE_FLAG_SECONDARY_ASR_ENABLED?: boolean;
}

export interface NewTransmissionMessage extends TransmissionMessage {
  data: {
    terminal_id: string;
    restaurant_code: string;
    session_id: string;
    media_format: string;
  };
}

export interface EndSessionTransmissionMessage extends TransmissionMessage {
  data: {
    restaurant_code: string;
    session_id: string;
  };
}

export interface AudioFrameTransmissionMessage extends TransmissionMessage {
  data: {
    payload: string;
    source_name: string;
  };
}

export enum BlockedReason {
  debounce = 'debounce',
  pendingAsr = 'pending asr',
}

export interface TextFrameTransmissionMessage extends TransmissionMessage {
  /**
   * HITL-1612 — LOCAL ONLY, never reaches the wire.
   *
   * Opt-out for the two paths that must still speak while the agent is outside
   * `Intercept`: the task-router menu-load failure (it tells the customer and
   * logs the agent out) and the staff-intent message. The socket middleware
   * destructures this off before building the outbound payload, so a consumer
   * never sees it. Deliberately at the top level rather than inside `data`,
   * which IS spread onto the wire.
   */
  bypassModeGate?: boolean;
  data: {
    status: 'hypothesis' | 'final' | 'TTS';
    payload: string;
    metadata?: any;
    hitl_mute?: boolean;
    blocked_status?: {
      blocked: boolean;
      reason?: BlockedReason;
      // For ORDERBOARD text events this may contain the original HITL
      // text event (including its metadata) that produced the TTS.
      // We intentionally keep this loose as it is a wire payload.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      source_event?: any;
    };
  };
}

export interface EntityMenuItem {
  id: number; // the id of the menu item or modifier and HITL will handle the time based menu and availabilty
  name: string;
  price: number;
  quantity: number;
  pos_specific_properties: {
    pos_id: number;
    pos_name?: string;
    modcode?: string;
  };
  children: EntityMenuItem[] | [];
  modifier_group_id: string;
  path_to_entity: string[] | [];
  is_auto_combined?: boolean;
  readback?: boolean;
  readbackName?: string;
}

export interface Hypothesis {
  dialog: string[];
  responses: string[];
  customer_intent: 'order' | 'other';
  confidence: number;
  order_items: EntityMenuItem[];
  is_brand_menu: boolean;
  // True on the gpt-context AI->HITL handoff flush frame (Fix #3 marker).
  handoff_flush?: boolean;
  brand_menu_order_items: IBrandMenuOrderItem[];
  loyalties?: ISendOrderLoyalty[];
  customer_info: {
    name: string;
  };
  // Populated by the AI (auto mode) only when the hypothesized order failed to
  // translate — same shape as the menu-module HTTP error response so HITL can
  // normalize and highlight it identically. Absent when the order is valid.
  menu_module_error?: IMUErrorResponse;
}

/**
 * Diagnostic card describing an item the AI heard the customer ask for that
 * does not exist in the menu and could not be resolved (after the say-again
 * retry + grace window). Populated by the producer only when the FF
 * `continue_ai_on_unresolved_items` is on. Unknown keys are forward-compatible.
 */
export interface NotOnMenuCard {
  item: string; // raw item text as the customer said it (lowercased / trimmed by producer)
  [key: string]: unknown; // forward-compat: ignore unknown producer-added keys
}

/**
 * Diagnostic card describing a constraint option (side, sauce, size, ...) the
 * customer requested that isn't valid for the parent item's constraint slot.
 */
export interface InvalidOptionLine {
  option: string; // option text the customer asked for, as inferred
  constraint: string | null; // constraint slot name (e.g. dipping_sauce); null when top-level
  is_top_level: boolean; // true when a tentative top-level item; false when a constraint child
  [key: string]: unknown; // forward-compat: ignore unknown producer-added keys
}

/**
 * Item whose requested quantity exceeds the per-item or brand-default threshold
 * (e.g. customer asked for 40 burgers; brand caps AI handling at 25). The AI
 * flagged the item but kept ordering under the FF; HITL should adjust manually.
 */
export interface QuantityEscalationItem {
  item_name: string; // top-level menu item name as resolved by the AI
  quantity: number; // quantity the AI inferred from the customer's utterance
  threshold: number; // threshold that was exceeded
  [key: string]: unknown;
}

/**
 * A required constraint with no default option that the customer's input
 * couldn't fill (e.g. meal requires a side but customer didn't name one).
 */
export interface NoDefaultOptionConstraint {
  constraint: string; // constraint slot name that has no default
  parent_item: string; // menu item that owns this constraint
  [key: string]: unknown;
}

/**
 * Coupon the customer mentioned that doesn't map to any menu offer.
 */
export interface UnmatchedCoupon {
  coupon: string; // coupon item as inferred by the AI
  has_related_offer: boolean; // always false today
  reason: string; // why the coupon wasn't applied (e.g. "no_related_offer_in_menu")
  [key: string]: unknown;
}

export type EntityType =
  | 'quantity'
  | 'menu_item'
  | 'discount'
  | 'coupon'
  | 'loyalty'
  | 'size';

export interface EntitySpan {
  start: number;
  end: number;
  type: EntityType;
  value: string;
}

export interface HypothesisTransmissionMessage extends TransmissionMessage {
  data: {
    hypotheses: Hypothesis[]; // will only have one hypothesis object in the hypotheses field for each session for now
    // Cumulative lists of items the AI could not resolve this session. Optional:
    // absent when the producer FF is off or nothing is unresolved. Treat absence
    // as an empty list. The producer ships the full cumulative list every event,
    // so consumers must replace (not append) and let it shrink on cart reset.
    not_on_menu_cards?: NotOnMenuCard[];
    invalid_option_lines?: InvalidOptionLine[];
    quantity_invalid?: QuantityEscalationItem[];
    no_default_option_constraints?: NoDefaultOptionConstraint[];
    unmatched_coupons?: UnmatchedCoupon[];
  };
}

export interface InfoTransmissionMessage extends TransmissionMessage {
  data:
    | {
        type: 'INFO' | 'METRIC' | 'LANGUAGE_SWITCH' | EventTypes.hypothesis;
        message: string;
        code?: string;
        metadata?: any; // This is really a json type per the spec, but... shocked TS doesn't have this.
      }
    | OrderProcessingMetricData;
}

export enum AgentInterceptionType {
  ORDER_CHANGED = 'ORDER_CHANGED',
  TYPING = 'TYPING',
  TTS_SENT = 'TTS_SENT',
  UNSUPERVISED_AI_ESCALATION = 'UNSUPERVISED_AI_ESCALATION',
  CANCEL_ORDER = 'CANCEL_ORDER',
  FINISH_ORDER = 'FINISH_ORDER',
}

export enum StaffInterceptionType {
  AGENT_REQUESTED_STAFF = 'AGENT_REQUESTED_STAFF',
}
export interface AgentInterceptionMessage extends TransmissionMessage {
  data: {
    type: AgentInterceptionType;
    description?: string;
  };
}

/**
 * HITL 3.0: the agent's per-turn response time exceeded the configured limit
 * during a takeover (see useAgentResponseTimeMetric). The first member of the
 * `agent_metric` family; its shape is unchanged by later members.
 */
export interface IAgentResponseTimeMetricData {
  metric: 'agent_response_time';
  status: 'EXCEEDED';
  // Configured limit that was exceeded (AGENT_RESPONSE_TIME_LIMIT_SECONDS * 1000).
  threshold_ms: number;
  // ISO timestamp of the customer utterance whose reply is overdue.
  awaiting_since: string;
  // The customer utterance the agent has not yet replied to.
  customer_utterance: string;
}

/**
 * HITL-1605: an agent-initiated agent-mode transition (Shadow /
 * Intercept), emitted from AgentModeSwitch at the press. Pure observability —
 * nothing reads it inbound, and it accompanies no interception, order or TTS.
 *
 * Only agent-initiated transitions appear here: the session-start reset to
 * `Shadow` emits nothing, because every session begins there by contract.
 * There is deliberately no duration field — dwell per mode is a query over
 * consecutive events, not a payload.
 */
export interface IAgentModeChangeMetricData {
  metric: 'agent_mode_change';
  from: AgentMode;
  to: AgentMode;
}

/**
 * HITL-1646: the first time in a session that the agent wrote into My Cart.
 *
 * The engagement signal that survives retiring `Supervise`. `agent_mode_change`
 * measures a button press; this measures the first time the agent actually did
 * something, which is what "engagement" was always meant to mean.
 *
 * One per session, and **it controls nothing**. Nothing reads it inbound, no
 * behaviour keys off it, and there is no flag on it beyond the feature's own.
 * `final_order_validation` was control-coupled and died in production; this does
 * not repeat that.
 */
export interface IAgentFirstCartEditMetricData {
  metric: 'agent_first_cart_edit';
  /** Which action broke the ice — tells us HOW agents start, not just when. */
  via: AgentFirstCartEditVia;
  /**
   * Engagement latency: milliseconds from the task landing on this agent's
   * screen to their first edit. `null` on a lane with no task assignment (the
   * single-restaurant flow), where there is no such moment to measure from.
   */
  ms_since_task_loaded: number | null;
  /**
   * The AI status colour at the moment they leaned in, mirroring what
   * `task_assigned` records for assignment — so "do agents engage at yellow, or
   * only at red?" becomes answerable rather than guessed at.
   */
  ai_status: string | null;
}

/**
 * HITL 3.0: an agent performance metric, discriminated by `data.metric`. This
 * interface is the contract of record for the family; today it has three
 * members, `agent_response_time` (the first), `agent_mode_change` (HITL-1605)
 * and `agent_first_cart_edit` (HITL-1646). A new metric is a new member here —
 * never a new `EventTypes` value.
 */
export interface IAgentMetricTransmissionMessage extends TransmissionMessage {
  data:
    | IAgentResponseTimeMetricData
    | IAgentModeChangeMetricData
    | IAgentFirstCartEditMetricData;
}

/**
 * HITL-18: the human agent's live working cart, emitted to the AI as read-only
 * input during takeover (the co-pilot input side). A full snapshot — not a
 * delta — and never a submit (must never be routed to POS).
 *
 * Item shape is `IBrandMenuOrderItem` (item name + nested `constraints[]`),
 * NOT the POS/order `ISendItemProperties` (id + pos_id + `children[]`). This is
 * deliberate: the AI's brand-menu cart is keyed by item NAME with constraints
 * (its own hypothesis even sends `id: 0` / empty `pos_specific_properties`), so
 * this is the shape the AI's hydrator (`generate_hydrated_cart`) actually
 * consumes. Built with the same `buildUnifiedMenuModuleOrder` converter the
 * brand-menu order submission uses, so it is 1:1 with a submitted order.
 */
export interface IAgentCartTransmissionMessage extends TransmissionMessage {
  data: {
    // Source-of-truth marker (HITL-17). `human` while the agent drives.
    cart_authority: 'human' | 'ai';
    final: boolean;
    // Correlation id: the AI echoes this in the ai_suggestion it sends back.
    request_id: string;
    items: IBrandMenuOrderItem[];
  };
}

/**
 * HITL 3.0: the human agent's explicit response to an `ai_suggestion` (the
 * "advise" side of the co-pilot). Emitted when the agent accepts or dismisses
 * the suggestion card. Correlated back to the suggestion via `suggestion_id`
 * and the originating cart via `request_id`. On accept-with-speech, `spoken_text`
 * carries the line spoken and `verbatim` marks whether it was spoken unchanged
 * (always true today — there is no edit-before-speak flow yet).
 */
export interface IAgentSuggestionResponseTransmissionMessage
  extends TransmissionMessage {
  data: {
    action: 'accepted' | 'dismissed';
    suggestion_id: string;
    request_id?: string;
    spoken_text?: string;
    verbatim?: boolean;
  };
}

export interface StaffInterceptionMessage extends TransmissionMessage {
  data: {
    type: StaffInterceptionType;
    description?: string;
  };
}

export interface StaffInfoTransmissionMessage extends TransmissionMessage {
  data: {
    payload: string;
    locale: string;
  };
}

interface ICouponApplyStatus {
  id: string;
  code: string;
  applied: boolean;
}

export interface CheckTransmissionMessage extends TransmissionMessage {
  data: {
    check?: {
      total: string;
      subtotal: string;
      tax: string;
      items?: any[];
    };
    error?: string;
    context?: string;
    display?: string;
    coupon_apply_status?: ICouponApplyStatus[];
    notification_enabled: boolean;
    request: string;
    status: CheckResponseTypes;
    transaction_id: string;
    request_id: string;
    final: boolean;
  };
}

export interface ICheckCouponTransmissionMessage extends TransmissionMessage {
  data: {
    couponno?: string;
    coupontext?: string;
    error?: string;
    status: CouponResponseTypes;
    transaction_id: string;
    request_id?: string;
  };
}

export interface ErrorTransmissionMessage extends TransmissionMessage {
  data: {
    message: string;
  };
}

export interface StaffInterVentionTransmissionMessage
  extends TransmissionMessage {
  restaurant_code: string;
  data: {
    type: string;
  };
}

export interface IStatusTransmissionMessage extends TransmissionMessage {
  data: {
    status: {
      code: number;
      type: string;
      message: string;
      additional_context?: string;
    };
    pos_partner: string;
    datetime: string;
    request_id?: string;
    result?: {
      transaction_id: string;
    };
  };
}

export interface IHITLSessionStartTransmissionMessage
  extends TransmissionMessage {
  data: {
    reason: string;
    brand_menu: boolean;
  };
}

export interface IHITLSessionEndTransmissionMessage
  extends TransmissionMessage {
  data: {
    reason: string;
  };
}

export interface IAIStatusTransmissionMessage extends TransmissionMessage {
  data: {
    status: AIStatusColors;
    message: string;
  };
}

/**
 * HITL 3.0: consolidated per-turn AI session state. The wire payload carries a
 * lot more (escalation, pending, order, …); we only type the fields the Pulse
 * card consumes today.
 */
export interface IAISessionStateTransmissionMessage
  extends TransmissionMessage {
  data: {
    // Conversation turn count for the live session.
    turn?: number;
    sentiment?: {
      label?: string;
    };
    reused_answers?: ReusedAnswers;
    express_mode?: {
      active?: boolean;
    };
  };
}

export interface IEntityAnnotationTransmissionMessage
  extends TransmissionMessage {
  data: {
    source_text_event_id: string;
    source_text: string;
    entities: EntitySpan[];
  };
}

// secondary_asr: the AI-side commit of an ASR utterance, carrying the committed
// text in `data.payload`. `metadata` (inherited, loosely typed) carries
// provenance: source, commit_reason, was_promoted_partial, confidence_nbest.
// HITL-1497: when `secondary_asr_enabled` is on these events ARE the customer
// transcript — the `text`/`final` frame for the same utterance is not rendered.
export interface IAsrPromotedTextTransmissionMessage
  extends TransmissionMessage {
  data: {
    payload: string;
    hitl_mute?: boolean;
  };
}

// secondary_asr: a recorded promoted-text occurrence — the customer transcript
// line itself when the flag is on. `source` distinguishes a secondary-engine
// promotion (badge-worthy) from the primary-source baseline commit; both are
// recorded, only `secondary` gets the icon.
//
// Recording is deliberately NOT gated on the feature flag. There is no
// flag-readiness gate in the app — the config flag initialises `false` and
// `useUnleashHooks` dispatches the real value only once Unleash resolves, while
// the socket (and session-buffer replay) may already be delivering events.
// Dropping on write would lose those lines permanently; keeping them costs a few
// hundred bytes and the selector simply ignores them while the flag is off.
export interface PromotedTextRef {
  id: string;
  text: string;
  timestamp: string;
  source?: string;
}

export interface IHITLEventConnectionTransmissionMessage
  extends TransmissionMessage {
  data: {
    message: string;
  };
}

export interface IHITLAgentFirstActivityTransmissionMessage
  extends TransmissionMessage {
  data: {
    message: string;
  };
}

export interface ICancelOrderTransmissionMessage extends TransmissionMessage {
  data: {
    check_id: string;
    source: string;
    store_id: string;
    order_id: string;
    request_id: string;
  };
}

export interface IAIEscalationTransmissionMessage extends TransmissionMessage {
  data: {
    hypotheses: Hypothesis[];
    number_of_turns_before_handoff: number;
    customer_requested_handoff: boolean;
    escalation_reason: string;
    // true => AI halted and a human must take over now; false/absent => a
    // recoverable escalation the AI keeps ordering through.
    ai_requested_hitl_takeover?: boolean;
  };
}

export interface ILanguageSwitchTransmissionMessage
  extends TransmissionMessage {
  data: {
    new_language: Languages;
  };
}

export interface MessagingState {
  isEstablishingConnection: boolean;
  isConnected: boolean;
  isAudioWSConnected: boolean;
  isEstablishingAudioWSConnection: boolean;
  eventWSConnectionMessage: string;
  audioWSConnectionMessage: string;
  eventWSConnectionSuccess: boolean;
  audioWSConnectionSuccess: boolean;
  eventWSConnectionDurationMs: number | null;
  audioWSConnectionDurationMs: number | null;
  sentTextFrames: TextFrameTransmissionMessage[];
  inProgressHypothesisTextFrames: TextFrameTransmissionMessage[];
  sentInfo: Pick<InfoTransmissionMessage, 'data'>[];
  sentEnd: EndSessionTransmissionMessage[];
  sendOrder: IOrderTransmissionMessage[];
  sendAgentCart: IAgentCartTransmissionMessage[];
  sendLoyalty: ILoyaltyTransmissionMessage[];
  sendAgentFirstActivity: any[];
  sendAgentFirstInterception: any[];
  sendAgentInterception: any[];
  sendAgentMetric: IAgentMetricTransmissionMessage[];
  sendAgentSuggestionResponse: IAgentSuggestionResponseTransmissionMessage[];
  sendStaffInterception: any[];
  // HITL-1492: websocket reconnect ATTEMPTS, surfaced to Redux so the frozen
  // issue-report context can carry them as an `Audio - Choppy` ranking signal. They
  // live as closure variables inside socketMiddleware, which no selector can reach.
  //
  // Attempts, not completed reconnections — and named for it. socketMiddleware's retry
  // loop re-enters through `onclose` and records once per attempt, so one flaky episode
  // needing four tries counts four. That is the signal we want (a session that never
  // recovers is the worst case, and would score ZERO on completed reconnections), but
  // the old name `wsReconnects` read as "reconnected N times", which it never meant.
  //
  // RESET AT SESSION START. Without that it accumulates for the life of the tab, so an
  // agent hours into a shift ships the same large count on every report however clean
  // the current car is — and since it only ever climbs, it would rank `Audio - Choppy`
  // progressively higher as the shift wears on.
  wsReconnectAttempts: { event: number; audio: number };
  // HITL-1492: when the most recent staff intervention landed. `isStaffIntervention`
  // is a bare boolean and cannot answer "how long ago", which is the ranking signal
  // for `Unnecessary Staff Intervention` — 52% of all reports filed today.
  staffInterventionAt: string | null;
  finalTextFrames: TextFrameTransmissionMessage[];
  startFrame: NewTransmissionMessage | null;
  hypothesisFrame: HypothesisTransmissionMessage | null;
  handledHypothesisFrames: Record<string, number[]>;
  currentSessionId: string;
  isRestaurantStaffJoined: Record<string, boolean>;
  isAIActive: boolean;
  isPlaying: boolean;
  isNewSession: boolean;
  isCarPresent: boolean;
  isStaffIntervention: boolean;
  isStaffIntervenedBeforeCarExit: boolean;
  sessionSource: SessionSource;
  isTTSOn: boolean;
  sentTTSRequest: TransmissionMessage[];
  sentError: ErrorTransmissionMessage[];
  restaurantStaffIntervention: RestaurantStaffInterventionStatus;
  sendHITLSessionStart: IHITLSessionStartTransmissionMessage[];
  sendHITLSessionEnd: IHITLSessionEndTransmissionMessage[];
  sendHITLEventConnection: IHITLEventConnectionTransmissionMessage[];
  sbFinalTextFrames: TextFrameTransmissionMessage[]; //Received via session buffer API regarding conversations happened before restaurant specific HITL page
  sbInProgressHypothesisTextFrames: TextFrameTransmissionMessage[]; //Received via session buffer API regarding conversations happened before restaurant specific HITL page
  sendCancelOrder: ICancelOrderTransmissionMessage[];
  entityAnnotationsMap: Record<string, EntitySpan[]>;
  // secondary_asr (HITL-1497): every promoted-text occurrence, primary and
  // secondary. With `secondary_asr_enabled` on these ARE the customer transcript
  // lines; with it off nothing reads them. Deduped by event id. `sb*` is the
  // session-buffer (pre-HITL replay) twin, mirroring the finalTextFrames split.
  promotedTextEvents: PromotedTextRef[];
  sbPromotedTextEvents: PromotedTextRef[];
}

export interface BufferedAudioState {
  frames: AudioFrameTransmissionMessage[];
  indexLocations: { [seq: number]: number };
  bufferedSeqs: number[];
  lastBufferFlush: number;
}

export const initialState: MessagingState = {
  isEstablishingConnection: false,
  isConnected: false,
  isAudioWSConnected: false,
  isEstablishingAudioWSConnection: false,
  eventWSConnectionMessage: '',
  audioWSConnectionMessage: '',
  eventWSConnectionSuccess: false,
  audioWSConnectionSuccess: false,
  eventWSConnectionDurationMs: null,
  audioWSConnectionDurationMs: null,
  // Seeded with the OUT_OF_SESSION sentinel, not a UUID. This is the last rung
  // of the session_id fallback chain used when stamping events, so a UUID here
  // meant out-of-session activity (e.g. a supervisor validating menu items in
  // pure HITL with no car present) got attributed to a plausible-looking
  // session that never existed. The sentinel says so honestly instead.
  // messageReceived() overwrites this from the first real inbound frame.
  currentSessionId: OUT_OF_SESSION,
  sentTextFrames: [],
  sentInfo: [],
  sentEnd: [],
  sendOrder: [],
  sendAgentCart: [],
  sendLoyalty: [],
  sendAgentFirstActivity: [],
  sendAgentFirstInterception: [],
  sendAgentInterception: [],
  sendAgentMetric: [],
  sendAgentSuggestionResponse: [],
  wsReconnectAttempts: { event: 0, audio: 0 },
  staffInterventionAt: null,
  sendStaffInterception: [],
  inProgressHypothesisTextFrames: [],
  finalTextFrames: [],
  startFrame: null,
  hypothesisFrame: null,
  handledHypothesisFrames: {},
  isRestaurantStaffJoined: {},
  isAIActive: false, // It's true when the restaurant has AI on. It is false when restaurant staff intervention happens.
  isPlaying: false,
  isNewSession: false,
  isCarPresent: false,
  isStaffIntervention: false,
  isStaffIntervenedBeforeCarExit: false,
  sessionSource: SessionSource.hitl,
  isTTSOn: false,
  sentTTSRequest: [],
  sentError: [],
  restaurantStaffIntervention: RestaurantStaffInterventionStatus.initial,
  sendHITLSessionStart: [],
  sendHITLSessionEnd: [],
  sendHITLEventConnection: [],
  sbFinalTextFrames: [],
  sbInProgressHypothesisTextFrames: [],
  sendCancelOrder: [],
  entityAnnotationsMap: {},
  promotedTextEvents: [],
  sbPromotedTextEvents: [],
};

interface IBufferMessagesResponseType {
  events: TransmissionMessage[];
}

export const getSessionBufferMessages = createAsyncThunk(
  'session/bufferMessages',
  async (
    {
      isCurrentTaskUnsupervised = false,
      timeout = 60000,
      checkAIMessages = false,
    }: {
      isCurrentTaskUnsupervised?: boolean;
      timeout?: number;
      checkAIMessages?: boolean;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    const {
      taskRouter,
      config: {
        SESSION_BUFFER_API,
        SESSION_BUFFER_API_AUTHENTICATION,
        FEATURE_FLAG_SECONDARY_ASR_ENABLED,
      },
      ai,
    } = getState();

    // Gate purely on the caller's flag; the sticky ai.isAIEscalation/requiresTakeover
    // leak across a RED->plain-HITL task switch (completeTRSetup passes the correct
    // value, incl. the HITL-1356 unsupervised-AI case).
    let agent_types = '';
    if (checkAIMessages) {
      agent_types = AgentTypes.llm;
    } else {
      agent_types =
        AgentTypes.orderBoard +
        `${
          isCurrentTaskUnsupervised
            ? ',' + AgentTypes.llm + ',' + AgentTypes.menuModule
            : ''
        }`;
    }

    Promise.race([
      get({
        url: `${SESSION_BUFFER_API}/sessions/${taskRouter.taskAssignedSessionId}/events?agent_type=${agent_types}`,
        headers: {
          Authorization: `Bearer ${SESSION_BUFFER_API_AUTHENTICATION}`,
        },
        successCallback: (response: { data: IBufferMessagesResponseType }) => {
          const receivedEvents = response?.data?.events || [];
          const bufferedMessages = isCurrentTaskUnsupervised
            ? filterBufferedEvents(receivedEvents)
            : receivedEvents;

          if (bufferedMessages.length) {
            logger.debug({
              message:
                'Received & filtered out the following buffered messages',
              count: bufferedMessages.length,
            });
            dispatch(
              messagingActions.messageReceived({
                messages: bufferedMessages,
                // HITL-1604: no `isCartEmpty` here on purpose. This payload is a
                // REPLAY, so the agentMode slice discards it before looking at
                // the cart — a buffered car_enter is history, not a new
                // customer. An absent flag also fails safe there (no reset).
                isSessionBufferedMessages: true,
                taskRouter,
                ai,
                FEATURE_FLAG_SECONDARY_ASR_ENABLED,
              })
            );
          }
          dispatch(
            updateNonCriticalCheckScore({
              name: NonCriticalChecksNames.apiSessionBuffer,
              value: 1,
            })
          );
        },
        errorCallback: (error) => {
          dispatch(
            updateNonCriticalCheckScore({
              name: NonCriticalChecksNames.apiSessionBuffer,
              value: 0,
            })
          );
          logger.warn({
            message: 'Failed to get buffered messages',
            error,
          });
          return rejectWithValue(error);
        },
      }),
      new Promise((resolve) => {
        dispatch(
          updateNonCriticalCheckScore({
            name: NonCriticalChecksNames.apiSessionBuffer,
            value: 0,
          })
        );
        setTimeout(
          resolve,
          timeout,
          new Error(
            'Timeout to get buffered events messages from Session Buffer API'
          )
        );
      }),
    ]).then((res) => {
      if (res instanceof Error) {
        logger.warn(res);
      }
    });
  }
);

type BaseNetworkCallLogsData = {
  url: string;
  message: string;
  /** time in milliseconds */
  duration: number;
};

type NetworkCallLogsData = BaseNetworkCallLogsData &
  (
    | {
        /** network calls made via which api */
        via: 'websocket' | 'fetch' | 'axios' | 'axios_api_wrapper';
      }
    | {
        /** graphql operation */
        graphQLOperation: string;
        /** network calls made via which api */
        via: 'graphql_sdk';
      }
  );

export const sendNetworkCallLogs = createAsyncThunk(
  'messaging/sendNetworkCallLogs',
  async (
    { message, ...metadata }: NetworkCallLogsData,
    { getState, dispatch }
  ) => {
    const { config, sessionBoundary, messages, restaurant, taskRouter } =
      getState();
    const isWebSocketConnected = selectIsWebSocketConnected(getState());

    const infoData: Pick<InfoTransmissionMessage, 'data'> = {
      data: { type: 'METRIC', message, metadata },
    };

    if (isWebSocketConnected) {
      dispatch(messagingActions.sendInfo(infoData));
      return;
    }

    // send event via event backend's REST API
    const infoEvent: InfoTransmissionMessage = {
      id: uuidv4(),
      seq: infoSeqIdCounter.increment(),
      event: EventTypes.info,
      session_id:
        taskRouter?.taskAssignedSessionId ||
        sessionBoundary?.sessionId ||
        messages?.currentSessionId,
      agent_type: AgentTypes.HITL,
      agent_id: agent.getAgentId(),
      timestamp: new Date().toISOString(),
      metadata: {
        environment: readEnvVariable('DEPLOY_ENV') || 'unknown',
      },
      restaurant_code:
        restaurant.selectedRestaurantDetails?.restaurantCode ||
        DUMMY_RESTAURANT_CODE,
      ...infoData,
    };

    const url = `${config.EVENT_API}/v1/event`;

    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${config.EVENT_API_BASIC_AUTH_PARAM}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(infoEvent),
    }).catch((err) => {
      logger.warn(err);
    });
  }
);

export const sendTestSignal = createAsyncThunk(
  'messaging/sendTestSignal',
  async (undefined, { getState, dispatch }) => {
    const {
      config,
      sessionBoundary: { sessionId },
      messages: { currentSessionId },
    } = getState();

    // send event via event backend's REST API
    const infoEvent: InfoTransmissionMessage = {
      id: uuidv4(),
      seq: infoSeqIdCounter.increment(),
      event: EventTypes.info,
      session_id: sessionId || currentSessionId || 'dummy_session_id',
      agent_type: AgentTypes.HITL,
      agent_id: agent.getAgentId(),
      timestamp: new Date().toISOString(),
      metadata: {
        environment: readEnvVariable('DEPLOY_ENV') || 'unknown',
      },
      data: {
        type: 'METRIC',
        message: 'test rest api signal from hitl',
      },
    };

    const url = `${config.EVENT_API}/v1/event`;

    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${config.EVENT_API_BASIC_AUTH_PARAM}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(infoEvent),
    }).catch((err) => {
      logger.warn(err);
    });
  }
);

export const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    startConnecting: (state) => {
      state.isConnected = false;
      state.isEstablishingConnection = true;
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
      state.isEstablishingConnection = false;
      state.eventWSConnectionMessage = '';
      state.eventWSConnectionSuccess = false;
    },
    connectionLost: (state) => {
      state.isConnected = false;
      state.isEstablishingConnection = true;
    },
    closeConnection: (state) => {
      state.isConnected = false;
      state.isEstablishingConnection = false;
      state.eventWSConnectionMessage = '';
    },
    setEventWSConnectionMessage: (state, action: PayloadAction<string>) => {
      state.eventWSConnectionMessage = action.payload;
      state.eventWSConnectionSuccess = false;
    },
    setAudioWSConnectionMessage: (state, action: PayloadAction<string>) => {
      state.audioWSConnectionMessage = action.payload;
      state.audioWSConnectionSuccess = false;
    },
    setEventWSConnectionSuccess: (state, action: PayloadAction<boolean>) => {
      state.eventWSConnectionSuccess = action.payload;
    },
    setAudioWSConnectionSuccess: (state, action: PayloadAction<boolean>) => {
      state.audioWSConnectionSuccess = action.payload;
    },
    startAudioWSConnecting: (state) => {
      state.isAudioWSConnected = false;
      state.isEstablishingAudioWSConnection = true;
    },
    audioWSConnectionEstablished: (state) => {
      state.isAudioWSConnected = true;
      state.isEstablishingAudioWSConnection = false;
      state.audioWSConnectionMessage = '';
      state.audioWSConnectionSuccess = false;
    },
    setEventWSConnectionDuration: (state, action: PayloadAction<number>) => {
      state.eventWSConnectionDurationMs = action.payload;
    },
    setAudioWSConnectionDuration: (state, action: PayloadAction<number>) => {
      state.audioWSConnectionDurationMs = action.payload;
    },
    audioWSConnectionLost: (state) => {
      state.isAudioWSConnected = false;
      state.isEstablishingAudioWSConnection = true;
    },
    closeAudioWSConnection: (state) => {
      state.isAudioWSConnected = false;
      state.isEstablishingAudioWSConnection = false;
      state.audioWSConnectionMessage = '';
    },
    messageReceived: (state, action: PayloadAction<IMessageReceived>) => {
      const {
        messages = [],
        taskRouter,
        isSessionBufferedMessages,
        FEATURE_FLAG_SECONDARY_ASR_ENABLED: secondaryAsrEnabled = false,
      } = action.payload;
      messages.forEach((message) => {
        if (
          [
            EventTypes.check,
            EventTypes.orderStatus,
            EventTypes.loyaltyStatus,
            EventTypes.connect,
            EventTypes.disconnect,
            EventTypes.aiEscalation,
            EventTypes.aiSessionState,
            EventTypes.order,
            EventTypes.aiSuggestion,
          ].includes(message.event)
        ) {
          //These messages are handled in other slices
          return;
        }

        if (
          taskRouter?.currentTaskId &&
          taskRouter?.taskAssignedSessionId !== message.session_id
        ) {
          //If the current task is from task-router and if the message session is not matching with
          //task-router current task session, don't process the received message - exception is TTS ON/OFF messages
          if (![EventTypes.TTSOff, EventTypes.TTSOn].includes(message.event))
            return;
        }

        if (
          message.session_id !== OUT_OF_SESSION &&
          state.currentSessionId !== message.session_id
        ) {
          state.currentSessionId = message.session_id;
          state.sessionSource = SessionSource.communicator;
        }
        switch (message.event) {
          case EventTypes.start:
            // TODO handle new start frame
            state.startFrame = message as NewTransmissionMessage;
            break;
          // WS-1 (HITL-1411): audio no longer flows through Redux. The socket
          // middleware plays guest (ORDERBOARD) audio directly via the
          // `audioPlayer` singleton and never dispatches audio frames, so there
          // is no `EventTypes.audio` case here anymore.
          case EventTypes.text:
            const textEvent = message as TextFrameTransmissionMessage;
            // TODO identify why we're getting an echo
            if (state.sentTextFrames.map((f) => f.id).includes(textEvent.id)) {
              break;
            }
            if (isSessionBufferedMessages) {
              if (textEvent.data.status === 'hypothesis') {
                state.sbInProgressHypothesisTextFrames.push(textEvent);
              } else if (textEvent.data.status === 'TTS') {
                state.sbFinalTextFrames.push(textEvent);
                const textPayload = textEvent.data.payload;
                if (!textEvent.data.hitl_mute && textPayload) {
                  cyborgAlert(textPayload);
                }
              } else {
                state.sbFinalTextFrames.push(textEvent);
                // Clear out all "in progress" text frames — unless secondary ASR
                // owns the committed line, in which case the promotion clears
                // them (see the asrPromotedText case). Exception: a blank final
                // is never committed by the AI, so no promotion will ever
                // arrive to retire the partial — clear it here.
                if (!secondaryAsrEnabled || !textEvent.data.payload?.trim()) {
                  state.sbInProgressHypothesisTextFrames.length = 0;
                }
              }
              break;
            }

            // TODO check seq id is larger than last
            if (textEvent.data.status === 'hypothesis') {
              state.inProgressHypothesisTextFrames.push(textEvent);
            } else if (textEvent.data.status === 'TTS') {
              state.finalTextFrames.push(textEvent);
              const textPayload = textEvent.data.payload;
              if (!textEvent.data.hitl_mute && textPayload) {
                cyborgAlert(textPayload);
              }
            } else {
              state.finalTextFrames.push(textEvent);
              // Clear out all "in progress" text frames — unless secondary ASR
              // owns the committed line, in which case the promotion clears them
              // (see the asrPromotedText case). Clearing here while the
              // replacement is still in flight would leave the transcript with
              // no line at all for the duration of the promotion lag.
              // Exception: a blank final (customer noise) is never committed by
              // the AI, so no promotion will ever arrive to retire the partial
              // — clear it here or it sits stale until the next commit.
              if (!secondaryAsrEnabled || !textEvent.data.payload?.trim()) {
                state.inProgressHypothesisTextFrames.length = 0;
              }
            }
            break;
          case EventTypes.hypothesis:
            const hypothesisFrame = message as HypothesisTransmissionMessage;
            if (state.currentSessionId === hypothesisFrame.session_id) {
              state.hypothesisFrame = message as HypothesisTransmissionMessage;
            }
            break;
          case EventTypes.startSession:
            state.isNewSession = true;
            state.isStaffIntervention = false;
            // HITL-1492: must move in lockstep with the boolean above. Left set, it
            // reports the PREVIOUS session's intervention as if it had just happened.
            state.staffInterventionAt = null;
            // HITL-1492: reconnect attempts are a per-session signal. Left running,
            // they accumulate for the life of the tab.
            state.wsReconnectAttempts = { event: 0, audio: 0 };
            state.restaurantStaffIntervention =
              RestaurantStaffInterventionStatus.initial;
            state.isStaffIntervenedBeforeCarExit = false;
            break;
          case EventTypes.carEnter: {
            state.isNewSession = true;
            state.isCarPresent = true;
            state.restaurantStaffIntervention =
              RestaurantStaffInterventionStatus.initial;
            if (!isSessionBufferedMessages) {
              state.inProgressHypothesisTextFrames = [];
              state.finalTextFrames = [];
              state.entityAnnotationsMap = {};
              state.promotedTextEvents = [];
              // HITL-1492: same scope as the transcript cleared above — these are
              // per-session issue-report signals, and a replayed carEnter from the
              // session buffer must not zero the live counters.
              state.wsReconnectAttempts = { event: 0, audio: 0 };
            }
            state.isStaffIntervenedBeforeCarExit = false;
            break;
          }
          case EventTypes.carExit:
            state.isNewSession = false;
            state.isCarPresent = false;
            state.isStaffIntervenedBeforeCarExit = state.isStaffIntervention;
            state.isStaffIntervention = false;
            // HITL-1492: in lockstep with the boolean — the departed car's
            // intervention must not read as recent for the next one.
            state.staffInterventionAt = null;
            // The departed car's hypothesis must not outlive it: the HITL 3.0
            // car-exit reset flips isAIAutoMode back on (CustomerPulse), and a
            // lingering frame would then be re-applied by useHypothesisFrames —
            // clearCart() + rebuild — reverting any agent edits made during an
            // intercept. The session-boundary sessionId and the (still false)
            // finalOrderSubmitted don't guard this window, so the frame itself
            // has to go.
            state.hypothesisFrame = null;
            break;
          case EventTypes.info:
            if (
              (message as StaffInfoTransmissionMessage).data.payload ===
              'StaffInterVention'
            ) {
              state.isStaffIntervention = true;
              // HITL-1492: this is the SECOND path that raises staff intervention
              // (the other is EventTypes.staffIntervention below), and it drives the
              // UI identically through useIntervention. Stamping only there left
              // `ms_since_staff_intervention` null — or worse, holding an older
              // session's value — while an intervention was happening right now.
              state.staffInterventionAt =
                message.timestamp || new Date().toISOString();
              state.isAIActive = false;
              state.hypothesisFrame = null;
              break;
            }
            break;
          case EventTypes.TTSOff:
            state.isTTSOn = false;
            break;
          case EventTypes.TTSOn:
            state.isTTSOn = true;
            break;
          case EventTypes.aiStatus:
            state.isAIActive = true;
            break;
          case EventTypes.transcriptHighlight: {
            const annotationMsg =
              message as IEntityAnnotationTransmissionMessage;
            const { source_text_event_id, entities } = annotationMsg.data ?? {};
            if (
              source_text_event_id &&
              entities &&
              !state.entityAnnotationsMap[source_text_event_id]
            ) {
              state.entityAnnotationsMap[source_text_event_id] = entities;
            }
            break;
          }
          case EventTypes.asrPromotedText: {
            // secondary_asr (HITL-1497): record EVERY promotion, primary and
            // secondary — with the flag on these are the customer transcript,
            // and dropping primary-source commits would drop most of the lines.
            // `source` is kept so the renderer can badge only secondary ones.
            // Recording stays unconditional (see PromotedTextRef for why).
            // Deduped by event id against echo/replay.
            const promotedMsg = message as IAsrPromotedTextTransmissionMessage;
            // Stored as-is: the AI computes entity-highlight offsets on the
            // unstripped payload (PRVA-12832), so trimming here would shift
            // every highlight on the rendered line. Trim decides emptiness only.
            const text = promotedMsg.data?.payload ?? '';
            if (!text.trim()) break;

            const ref: PromotedTextRef = {
              id: promotedMsg.id,
              text,
              timestamp: promotedMsg.timestamp,
              source: promotedMsg.metadata?.source,
            };
            const target = isSessionBufferedMessages
              ? state.sbPromotedTextEvents
              : state.promotedTextEvents;
            if (!target.some((p) => p.id === ref.id)) {
              target.push(ref);
            }
            // The promotion is the committed line now, so it — not the
            // `text`/`final` frame — retires the in-progress hypothesis. Only
            // when the flag is on; otherwise the final already cleared them.
            if (secondaryAsrEnabled) {
              if (isSessionBufferedMessages) {
                state.sbInProgressHypothesisTextFrames.length = 0;
              } else {
                state.inProgressHypothesisTextFrames.length = 0;
              }
            }
            break;
          }
          case EventTypes.staffIntervention:
            state.isStaffIntervention = true;
            // HITL-1492: the boolean cannot answer "how long ago", which is the
            // ranking signal for `Unnecessary Staff Intervention`. Stamped from the
            // frame's own timestamp where present so a replayed/buffered frame does
            // not read as having just happened.
            state.staffInterventionAt =
              message.timestamp || new Date().toISOString();
            state.isAIActive = false;
            state.hypothesisFrame = null;
            break;
          default:
            // Suppress error for dynamic property access or check property existence before accessing 'type'
            // @ts-ignore
            if (message['type'] !== 'ping') {
              logger.debug({
                message: `Received an unhandled message event with type ${message.event}`,
              });
            }
        }
      });
    },
    sendMessage: (
      state,
      action: PayloadAction<TextFrameTransmissionMessage>
    ) => {
      state.sentTextFrames.push(action.payload);
    },
    sendInfo: (
      state,
      action: PayloadAction<Pick<InfoTransmissionMessage, 'data'>>
    ) => {
      state.sentInfo.push(action.payload);
    },
    sendAgentFirstActivity: (
      state,
      action: PayloadAction<IHITLAgentFirstActivityTransmissionMessage>
    ) => {
      state.sendAgentFirstActivity.push(action.payload);
    },
    sendAgentFirstInterception: (
      state,
      action: PayloadAction<Pick<AgentInterceptionMessage, 'data'>>
    ) => {
      state.sendAgentFirstInterception.push(action.payload);
    },
    sendAgentInterception: (
      state,
      action: PayloadAction<Pick<AgentInterceptionMessage, 'data'>>
    ) => {
      state.sendAgentInterception.push(action.payload);
    },
    sendAgentMetric: (
      state,
      action: PayloadAction<Pick<IAgentMetricTransmissionMessage, 'data'>>
    ) => {
      state.sendAgentMetric.push(
        action.payload as IAgentMetricTransmissionMessage
      );
    },
    sendAgentSuggestionResponse: (
      state,
      action: PayloadAction<
        Pick<IAgentSuggestionResponseTransmissionMessage, 'data'>
      >
    ) => {
      state.sendAgentSuggestionResponse.push(
        action.payload as IAgentSuggestionResponseTransmissionMessage
      );
    },
    sendStaffInterception: (
      state,
      action: PayloadAction<Pick<StaffInterceptionMessage, 'data'>>
    ) => {
      state.sendStaffInterception.push(action.payload);
    },
    /**
     * HITL-1492: record a websocket reconnect ATTEMPT so the frozen issue-report
     * context can carry it. Write-only — this changes no reconnect behaviour.
     *
     * Zeroed at session start by the startSession / carEnter cases above; see the
     * field comment for why per-session scope matters.
     */
    wsReconnectAttemptRecorded: (
      state,
      action: PayloadAction<{ socket: 'event' | 'audio' }>
    ) => {
      state.wsReconnectAttempts[action.payload.socket] += 1;
    },
    handleHypothesisFrame: (
      state,
      action: PayloadAction<{ sessionId: string; index: number }>
    ) => {
      // the order item will be pushed to hypothesis array in sequence so keep the idex of order item already handled to reduce duplicate rendering
      state.handledHypothesisFrames[action.payload.sessionId].push(
        action.payload.index
      );
    },
    sendEndSession: (
      state,
      action: PayloadAction<EndSessionTransmissionMessage>
    ) => {
      state.sentEnd.push(action.payload);
    },
    sendOrder: (state, action: PayloadAction<IOrderTransmissionMessage>) => {
      state.sendOrder.push(action.payload);
    },
    sendAgentCart: (
      state,
      action: PayloadAction<Pick<IAgentCartTransmissionMessage, 'data'>>
    ) => {
      state.sendAgentCart.push(action.payload as IAgentCartTransmissionMessage);
    },
    sendLoyalty: (
      state,
      action: PayloadAction<ILoyaltyTransmissionMessage>
    ) => {
      state.sendLoyalty.push(action.payload);
    },
    closeRestaurantStaffJoined: (
      state,
      action: PayloadAction<{ sessionId: string | null }>
    ) => {
      if (state.isRestaurantStaffJoined && action.payload.sessionId) {
        state.isRestaurantStaffJoined[action.payload.sessionId] = false;
      }
    },
    clearMessages: (state) => {
      state.finalTextFrames = [];
      state.inProgressHypothesisTextFrames = [];
      state.sbFinalTextFrames = [];
      state.sbInProgressHypothesisTextFrames = [];
      state.sentTextFrames = [];
      state.sendAgentFirstActivity = [];
      state.sendAgentFirstInterception = [];
      state.sendAgentInterception = [];
      state.sendStaffInterception = [];
      state.entityAnnotationsMap = {};
      state.promotedTextEvents = [];
      state.sbPromotedTextEvents = [];
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      // WS-1 (HITL-1411): pure reducer. The audioPlayer start/stop lifecycle is
      // driven by the socket middleware intercepting this action, so the reducer
      // no longer touches a non-serializable player instance.
      state.isPlaying = action.payload;
    },
    setIsAIActive: (state, action: PayloadAction<boolean>) => {
      state.isAIActive = action.payload; // isAIActive is reset by getAIControlPanelStatus when restaurant is selected and when hitl session ends
    },
    setRestaurantStaffIntervention: (
      state,
      action: PayloadAction<RestaurantStaffInterventionStatus>
    ) => {
      state.restaurantStaffIntervention = action.payload;
    },
    resetCarState: (state) => {
      state.isNewSession = false;
      state.isCarPresent = false;
      state.restaurantStaffIntervention =
        RestaurantStaffInterventionStatus.initial;
      state.isStaffIntervention = false;
      // HITL-1492: in lockstep with the boolean. Every dispatch site of this action
      // is a session-is-over boundary, so a retained timestamp would report a dead
      // session's intervention as recent.
      state.staffInterventionAt = null;
      // Drop the adopted session id too. Every dispatch site of this action is a
      // session-is-over boundary (task canceled/completed, TaskRouter lobby
      // re-init, restaurant switch), and nothing else ever cleared this field —
      // so it used to retain the last real session's id indefinitely and stamp
      // subsequent out-of-session events with it.
      state.currentSessionId = OUT_OF_SESSION;
    },
    setStaffIntervention: (state, action: PayloadAction<boolean>) => {
      state.isStaffIntervention = action.payload;
      // HITL-1492: in lockstep with the boolean. The task-router path
      // (useTaskRouterWS) raises intervention through here rather than through a
      // frame, so without this an intervention adopted with a task carries no
      // timestamp at all.
      state.staffInterventionAt = action.payload
        ? new Date().toISOString()
        : null;
    },
    sendTTSRequest: (state, action: PayloadAction<TransmissionMessage>) => {
      state.sentTTSRequest.push(action.payload);
    },
    sendError: (state, action: PayloadAction<ErrorTransmissionMessage>) => {
      state.sentError.push(action.payload);
    },
    resetHypothesisFrame: (state) => {
      state.hypothesisFrame = null;
    },
    carEnter: (state) => {
      state.isNewSession = true;
      state.isCarPresent = true;
      state.restaurantStaffIntervention =
        RestaurantStaffInterventionStatus.initial;
    },
    sendHITLSessionStart: (
      state,
      action: PayloadAction<IHITLSessionStartTransmissionMessage>
    ) => {
      state.sendHITLSessionStart.push(action.payload);
    },
    sendHITLSessionEnd: (
      state,
      action: PayloadAction<IHITLSessionEndTransmissionMessage>
    ) => {
      state.sendHITLSessionEnd.push(action.payload);
    },
    sendHITLEventConnection: (
      state,
      action: PayloadAction<IHITLEventConnectionTransmissionMessage>
    ) => {
      state.sendHITLEventConnection.push(action.payload);
    },
    sendCancelOrder: (
      state,
      action: PayloadAction<ICancelOrderTransmissionMessage>
    ) => {
      state.sendCancelOrder.push(action.payload);
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(selectStage.fulfilled, selectMenuVersion.fulfilled),
      (state, action) => {
        state.hypothesisFrame = null;
      }
    );
  },
});

export const messagingActions = messagingSlice.actions;
export const {
  setIsAIActive,
  setStaffIntervention,
  resetHypothesisFrame,
  sendOrder,
  sendLoyalty,
  sendHITLSessionStart,
  sendHITLSessionEnd,
  startAudioWSConnecting,
  audioWSConnectionEstablished,
  setEventWSConnectionDuration,
  setAudioWSConnectionDuration,
  audioWSConnectionLost,
  closeAudioWSConnection,
  sendCancelOrder,
  clearMessages,
} = messagingSlice.actions;

export default messagingSlice.reducer;
